import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getScreenerDiscoverySnapshot } from '@/lib/api'
import { filterDiscoveryItems } from '@/lib/screenerDiscovery'
import type { ScreenerDiscoveryFilters, ScreenerDiscoveryResponse } from '@/types'

export function useScreenerDiscovery(filters: ScreenerDiscoveryFilters, enabled = true) {
  const changePeriod = filters.changePeriod ?? '24h'
  const exchanges = useMemo(
    () => Array.from(new Set(filters.exchanges.map((exchange) => exchange.trim().toLowerCase()).filter(Boolean))).sort(),
    [filters.exchanges],
  )
  const select = useCallback((snapshot: ScreenerDiscoveryResponse) => ({
    ...snapshot,
    items: filterDiscoveryItems(snapshot.items, filters),
  }), [filters])

  // A keystroke must not rescan OI history. Refresh one full market snapshot
  // per exchange selection and OI period, then search/filter/sort it in memory.
  return useQuery({
    queryKey: ['screener-discovery-snapshot', exchanges, changePeriod],
    queryFn: ({ signal }) => getScreenerDiscoverySnapshot(exchanges, signal, changePeriod),
    select,
    enabled: enabled && exchanges.length > 0,
    refetchInterval: 15_000,
    staleTime: 10_000,
  })
}
